import { ActionsHook } from "@/lib/Components/Actions/ActionGroupButtons";
import { useNavigate } from "@tanstack/react-router";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import {
  ArchiveBoxIcon,
  EyeIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { VehicleListItem } from "@/app/Vehicles/GraphQL/vehicleListQuery";
import { ConfirmDeleteDialog } from "@/lib/Components/Dialog/ConfirmDeleteDialog";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import {
  archiveVehicleMutation,
  deleteVehicleMutation,
} from "@/app/Vehicles/GraphQL/vehicleMutations";
import { PlusCircleIcon } from "lucide-react";
import { VehiclePurchasableExtraDialog } from "@/app/Vehicles/Components/Dialogs/VehiclePurchasableExtraDialog";

export function useVehicleActions(): ActionsHook<VehicleListItem> {
  const navigate = useNavigate();
  const { open: openDeleteDialog } = useDialog(ConfirmDeleteDialog);
  const { mutateAsync: deleteVehicle } = useGqlMutation(deleteVehicleMutation);
  const { mutateAsync: archiveOffice } = useGqlMutation(archiveVehicleMutation);
  const { open: openPurchasableExtras } = useDialog(
    VehiclePurchasableExtraDialog,
  );

  return (vehicle) => {
    return [
      {
        label: "Actions",
        items: [
          {
            actions: [
              {
                label: "Apply Purchasable Extras",
                Icon: PlusCircleIcon,
                handleAction: async () => {
                  openPurchasableExtras({
                    vehicleId: vehicle.id,
                  });
                },
              },
            ],
          },
          {
            actions: [
              {
                label: "View",
                Icon: EyeIcon,
                handleAction: async () => {
                  await navigate({
                    to: ".",
                    search: (prev: any) => ({
                      ...prev,
                      vehicleId: vehicle.id,
                    }),
                  });
                },
              },
            ],
          },
          {
            actions: [
              {
                label: "Archive",
                Icon: ArchiveBoxIcon,
                isHiddenTopBar: true,
                color: "purple",
                handleAction: () => {
                  openDeleteDialog({
                    message:
                      "Are you sure you want to archive this vehicle? Archiving a vehicle will remove it from the list of active vehicles.",
                    buttonText: `Archive ${vehicle.name}`,
                    title: `Archive ${vehicle.name}`,
                    onDelete: async () => {
                      await archiveOffice({ id: vehicle.id });
                    },
                  });
                },
              },
              {
                label: "Delete",
                Icon: TrashIcon,
                isHiddenTopBar: true,
                color: "red",
                handleAction: () => {
                  openDeleteDialog({
                    message: "Are you sure you want to delete this vehicle?",
                    buttonText: `Delete ${vehicle.name}`,
                    title: `Delete ${vehicle.name}`,
                    onDelete: async () => {
                      await deleteVehicle({ id: vehicle.id });
                      await navigate({
                        from: "/$tenant/$orgId/supplier/{-$supplierId}",
                        to: "/$tenant/$orgId/supplier/{-$supplierId}/vehicles/create",
                        params: (prev) => prev,
                      });
                    },
                  });
                },
              },
            ],
          },
        ],
      },
    ];
  };
}
